import React, { useState } from "react";
import { Redirect, useHistory, Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import Edit from "./Edit.svg";
import Form from "./form";
import Page from "./page";
import PageHeader from "./pageheader";
import Title from "./title";
import _questions from "./_questions";
import genderCd from "./gendercd";
import poweredChoices from "./poweredChoices";
import { FullWidthDivider } from "../shared";

export default function Review({ mediaQuery, page, total, person }) {
  const isCIR = person !== undefined ? person.membership_type === "Creative in Residence" : false;
  const nextUrl = "/apply/submitted/";
  const submitUrl = "/api/applications/v4/application/submit/";
  const {
    data: {
      open_houses,
      results: [
        {
          submission,
          submission: { questions },
          submission: {
            references: { references }
          }
        }
      ]
    }
  } = useQuery(["application"]);
  const [form, setForm] = useState({ errors: null });
  const open_house =
    submission.open_house.id !== 0
      ? open_houses
        .filter((n) => n.id === submission.open_house.id)
        .reduce((_, m) => m.start_dttm, [])
      : "I will schedule later";
  function MembershipType() {
    function Label({ showBorder = true, children }) {
      return (
        <div
          style={{
            borderBottom: showBorder && !mediaQuery.isTabletOrMobile ? "1px solid #D4D4D4" : "",
            fontFamily: "BentonSans",
            fontWeight: "bold",
            gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "",
            paddingBottom: "1rem",
            textAlign: "left"
          }}
        >
          {children}
        </div>
      );
    }
    function Value({ showBorder = true, to, label, children }) {
      return (
        <div
          style={{
            borderBottom: showBorder ? "1px solid #D4D4D4" : "",
            gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "",
            paddingBottom: "1rem",
            textAlign: mediaQuery.isTabletOrMobile ? "left" : "right"
          }}
        >
          {children}
          <div style={{ display: "inline-block", marginLeft: "1rem" }}>
            <Link to={to} aria-label={label}>
              <Edit />
            </Link>
          </div>
        </div>
      );
    }
    return (
      <>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1rem 0" }}>
          <Label>{submission.membership_price.membership_type_cd} Membership</Label>
          <Value to={`/apply/`} label="Membership Type">
            ${parseInt(submission.membership_price.annual_fee_amt)} annually + $
            {parseInt(submission.membership_price.initiation_fee_amt)} one time initiation fee
          </Value>
          {submission.alliance.alliance === true && (
            <>
              <Label>The Battery Alliance</Label>
              <Value to={`/apply/alliance/`} label="Alliance">
                $900 annually
              </Value>
            </>
          )}
          {submission.alliance.alliance === false && (
            <>
              <Label>The Battery Alliance</Label>
              <Value to={`/apply/alliance/`} label="Alliance">
                No, access to The Battery only
              </Value>
            </>
          )}
          <Label showBorder={isCIR ? false : true}>Battery Powered Membership Level</Label>
          <Value
            to={`/apply/battery-powered-membership/`}
            label="Battery Powered"
            showBorder={isCIR ? false : true}
          >
            {poweredChoices[submission?.battery_powered_membership?.battery_powered_type]?.name}
          </Value>
          {isCIR === false && (
            <>
              <Label showBorder={false}>Open House</Label>
              <Value to={`/apply/open-house/`} showBorder={false} label="Open House">
                {submission.open_house.id === 0
                  ? open_house
                  : submission.open_house.id !== null
                    ? format(parseISO(open_house), "PPPP p")
                    : ""}
              </Value>
            </>
          )}
        </div>
        <FullWidthDivider style={{ marginBottom: "2rem" }} />
      </>
    );
  }
  function SectionTitle({ children }) {
    return (
      <div
        style={{
          color: "#011F31",
          fontFamily: "BentonModernDisplaySemiBold",
          fontSize: "30px",
          fontStyle: "normal",
          fontWeight: "400",
          gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "",
          letterSpacing: "-0.5px",
          lineHeight: "36px",
          marginBottom: mediaQuery.isTabletOrMobile ? 0 : "2rem",
          textAlign: "left"
        }}
      >
        {children}
      </div>
    );
  }
  function EditButton({ to }) {
    const history = useHistory();
    return (
      <div
        style={{
          textAlign: mediaQuery.isTabletOrMobile ? "left" : "right",
          gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : ""
        }}
      >
        <button
          style={{
            backgroundColor: "transparent",
            border: "1px solid #011F31",
            color: "#011F31",
            fontFamily: "TradeGothicCondensed",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 2,
            marginBottom: 0,
            padding: ".5rem",
            textTransform: "uppercase"
          }}
          onClick={() => history.push(to)}
        >
          Edit
        </button>
      </div>
    );
  }
  function Heading({ title, to }) {
    return (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "1rem",
          marginTop: "1rem",
          marginBottom: "1rem"
        }}
      >
        <SectionTitle>{title}</SectionTitle>
        <EditButton to={to} />
      </div>
    );
  }
  function Label({ children }) {
    return (
      <div
        style={{
          fontFamily: "BentonSans",
          fontWeight: "bold",
          textAlign: "left",
          gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : ""
        }}
      >
        {children}
      </div>
    );
  }
  function Value({ children }) {
    return (
      <div style={{ textAlign: "left", gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : "" }}>
        {children}
      </div>
    );
  }
  function InvitePartner() {
    const { first_name, last_name, email, mobile_phone, not_interested } = submission.partner;
    const {
      membership_price: { membership_type_cd }
    } = submission;
    const isPartner = membership_type_cd === "Partner";
    function NotInterested() {
      return (
        <>
          <Heading title="Invite Partner" to={`/apply/invite-partner/`} />
          <div style={{ textAlign: "left" }}>
            <p>My partner is not currently interested in joining</p>
            <FullWidthDivider style={{ marginBottom: "2rem" }} />
          </div>
        </>
      );
    }
    function Partner() {
      return (
        <>
          <Heading title="Invite Partner" to={`/apply/invite-partner/`} />
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
              marginBottom: "1rem"
            }}
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
                gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : ""
              }}
            >
              <Label>Name</Label>
              <Value>
                {first_name} {last_name}
              </Value>
              <Label>Email</Label>
              <Value>{email}</Value>
              <Label>Mobile Phone</Label>
              <Value>{mobile_phone}</Value>
            </div>
          </div>
          <FullWidthDivider style={{ marginBottom: "2rem" }} />
        </>
      );
    }
    return isPartner ? null : not_interested ? <NotInterested /> : <Partner />;
  }
  function BuildingCommunity() {
    const {
      nom_1_first_name,
      nom_1_last_name,
      nom_1_email,
      nom_1_reason,
      nom_2_first_name,
      nom_2_last_name,
      nom_2_email,
      nom_2_reason
    } = submission.community;
    return (
      <>
        <Heading title="Building Community" to="/apply/community" />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1rem",
            marginBottom: "1rem"
          }}
        >
          {nom_1_first_name && nom_1_last_name && nom_1_reason && nom_1_email && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
                gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : ""
              }}
            >
              <Label>Name</Label>
              <Value>
                {nom_1_first_name} {nom_1_last_name}
              </Value>
              <Label>Email</Label>
              <Value>{nom_1_email}</Value>
              <Label>Reason</Label>
              <Value>{nom_1_reason}</Value>
            </div>
          )}
          {nom_2_first_name && nom_2_last_name && nom_2_email && nom_2_reason && (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "1rem",
                gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : ""
              }}
            >
              <Label>Name</Label>
              <Value>
                {nom_2_first_name} {nom_2_last_name}
              </Value>
              <Label>Email</Label>
              <Value>{nom_2_email}</Value>
              <Label>Reason</Label>
              <Value>{nom_2_reason}</Value>
            </div>
          )}
        </div>
        <FullWidthDivider style={{ marginBottom: "2rem" }} />
      </>
    );
  }
  function PersonalInfo() {
    const {
      first_name,
      last_name,
      gender_cd,
      pronouns,
      date_of_birth_day,
      date_of_birth_month,
      date_of_birth_year,
      email,
      member_name,
      member_email,
      home_street_tx,
      home_city_tx,
      home_state_cd,
      home_postcode_tx,
      home_country_cd,
      business_name_tx,
      business_job_title_tx,
      business_street_tx,
      business_city_tx,
      business_state_cd,
      business_postcode_tx,
      business_country_cd,
      mobile_phone
    } = submission.personal_information;
    const home_address = `${home_street_tx}, ${home_city_tx}, ${home_state_cd}, ${home_postcode_tx}, ${home_country_cd}`;
    const business_address = `${business_street_tx}, ${business_city_tx}, ${business_state_cd}, ${business_postcode_tx}, ${business_country_cd}`;
    const needsPartner = submission.membership_price.membership_type_cd === "Partner";
    const hasPartner = !(
      (member_email === undefined || member_email === "") &&
      (member_name === undefined || member_name === "")
    );
    if (needsPartner && !hasPartner) {
      return <Redirect to={`/apply/personal-information/`} />;
    }
    return (
      <>
        <Heading title="Tell us about yourself" to={`/apply/personal-information/`} />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: "1rem",
            marginBottom: "1rem"
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
              gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : ""
            }}
          >
            <Label>Name</Label>
            <Value>
              {first_name} {last_name}
            </Value>
            <Label>Gender</Label>
            <Value>{genderCd[gender_cd]}</Value>
            <Label>Pronouns</Label>
            <Value>{pronouns}</Value>
            <Label>Birthday</Label>
            <Value>{`${date_of_birth_month}/${date_of_birth_day}/${date_of_birth_year}`}</Value>
            {submission.membership_price.membership_type_cd === "Partner" && (
              <>
                <Label>Partner</Label>
                <Value>
                  {member_name}, {member_email}
                </Value>
              </>
            )}
            <Label>Home Address</Label>
            <Value>{home_address}</Value>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "1rem",
              gridColumn: mediaQuery.isTabletOrMobile ? "span 2" : ""
            }}
          >
            <Label>Email</Label>
            <Value>{email}</Value>
            <Label>Phone</Label>
            <Value>{mobile_phone}</Value>
            <Label>Employer</Label>
            <Value>{business_name_tx}</Value>
            <Label>Job Title</Label>
            <Value>{business_job_title_tx}</Value>
            <Label>Business Address</Label>
            <Value>{business_address}</Value>
          </div>
        </div>
        <FullWidthDivider style={{ marginBottom: "2rem" }} />
      </>
    );
  }
  function Photo() {
    const {
      upload_headshot: {
        info: { uuid }
      }
    } = submission;
    const cdnUrl = `https://ucarecdn.com/${uuid}/-/format/jpeg/${uuid}.jpg`;
    return (
      <div
        style={{
          alignContent: "center",
          display: "flex",
          justifyContent: "center",
          marginBottom: "2rem",
          position: "relative"
        }}
      >
        <div
          style={{
            width: 200,
            height: 200,
            position: "relative",
            overflow: "hidden",
            borderRadius: "50%"
          }}
        >
          <img src={cdnUrl} alt="Your current profile" />
        </div>
        <Link to={`/apply/upload-headshot/`} aria-label="Upload headshot">
          <div
            style={{
              alignContent: "center",
              alignItems: "center",
              backgroundColor: "#E2DFD8",
              borderRadius: "50%",
              bottom: 0,
              display: "flex",
              height: 40,
              justifyContent: "center",
              position: "absolute",
              right: mediaQuery.isTabletOrMobile ? "20%" : "40%",
              width: 40
            }}
          >
            <Edit />
          </div>
        </Link>
      </div>
    );
  }
  function Questions({ questions }) {
    const answers = _questions
      .map((n, idx) => ({
        question: n.question,
        placeholder: n.placeholder,
        idx,
        answer: questions[`question${idx + 1}`]
      }))
      .filter((n) => !isCIR || (isCIR && n.idx !== 1));
    return (
      <>
        <Heading title="The nitty gritty" to={`/apply/questions/`} />
        <div style={{ textAlign: "left" }}>
          {answers.map((n) => (
            <div key={n.idx}>
              <Title style={{ marginBottom: ".5rem" }}>
                {n.question} {n.placeholder}
              </Title>
              <div style={{ marginBottom: "2rem" }}>{n.answer}</div>
            </div>
          ))}
        </div>
      </>
    );
  }
  function References() {
    return (
      <>
        <Heading title="References" to={`/apply/references/`} />
        <div style={{ textAlign: "left" }}>
          {references.map((n, idx) => (
            <div key={idx}>
              <Title style={{ marginBottom: ".5rem" }}>
                {n.first_name} {n.last_name}
              </Title>
              <div style={{ marginBottom: "2rem" }}>{n.mobile_phone}</div>
              <div style={{ marginBottom: "2rem" }}>{n.email}</div>
            </div>
          ))}
        </div>
        <FullWidthDivider style={{ marginBottom: "2rem" }} />
      </>
    );
  }
  function Errors({ errors }) {
    if (errors !== null) {
      const message = Object.keys(errors).reduce((p, k) => {
        return p + errors[k];
      }, "");
      return <div style={{ color: "red", marginBottom: "1rem" }}>{message}</div>;
    } else {
      return null;
    }
  }
  return (
    <Page>
      <PageHeader
        title={`APPLICATION PAGE ${page} OF ${total}`}
        tagline="Review your application"
      />
      <Errors errors={form.errors} />
      <Photo />
      <MembershipType />
      {isCIR ? null : <References />}
      <PersonalInfo />
      {isCIR ? null : <InvitePartner />}
      <BuildingCommunity />
      <Questions questions={questions} />
      <Form form={form} setForm={setForm} nextUrl={nextUrl} submitUrl={submitUrl}></Form>
    </Page>
  );
}
